import { useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import type { CustomerOrder, SalePaymentType } from "../../api/types";
import { StockItemFinder } from "./StockItemFinder";
import { TicketLines, ticketLinesProblem, ticketLinesTotal } from "./TicketLines";
import { useStockTicket } from "./useStockTicket";
import { eur } from "./shopFloorFormat";
import { EMPTY_CART, type ComposerCart } from "../orders/orderBuild";
import { useT } from "../../i18n/LanguageProvider";
import { t as translate } from "../../i18n/t";
import {
  DescribeBouquet,
  type DescribePhotoThumb,
} from "../orders/DescribeBouquet";

import { OrderWhenFields } from "./OrderWhenFields";
import { defaultOrderWhen, orderWhenPayload, type OrderWhen } from "./orderWhen";

const PHOTO_CAP = 10;

/**
 * G-46 GAP 1 — the counter order, WITHOUT leaving kiosk mode. A customer
 * standing at the till can be scheduled for any day: described bouquet
 * (G-15's DescribeBouquet, reused as the spec prescribes) + reference
 * photos, pickup or delivery, exact time or a window, the agreed price, and
 * a real deposit (amount + method → a ledger row, never a flag). Creation
 * itself is the one `createOrder` implementation on the server.
 */
export function ShopFloorOrderCreate({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (order: CustomerOrder) => Promise<void>;
}) {
  const { t } = useT();
  const [cart, setCart] = useState<ComposerCart>({ ...EMPTY_CART });
  const [customerNote, setCustomerNote] = useState("");
  const [photos, setPhotos] = useState<DescribePhotoThumb[]>([]);
  const [files, setFiles] = useState<Map<string, File>>(new Map());
  const [step, setStep] = useState<"bouquet" | "customer">("bouquet");

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [fulfillment, setFulfillment] = useState<"PICKUP" | "COURIER">(
    "PICKUP",
  );
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryPriceEur, setDeliveryPriceEur] = useState("");
  const [when, setWhen] = useState<OrderWhen>(defaultOrderWhen);
  const [depositEur, setDepositEur] = useState("");
  const [depositMethod, setDepositMethod] = useState<SalePaymentType>("CARD");
  // G-46 decision 3: the counter order takes stock items too (candles,
  // vases) — the SAME finder-and-ticket implementation as the checkout
  // (useStockTicket), so the pick cap and retry semantics cannot drift.
  const [finderOpen, setFinderOpen] = useState(false);
  const ticket = useStockTicket(finderOpen);

  const itemsTotal = ticketLinesTotal(ticket.lines);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (list: File[]) => {
    const next = new Map(files);
    const thumbs = [...photos];
    for (const file of list) {
      if (thumbs.length >= PHOTO_CAP) break;
      // UUID keys, the OrderBuildPage pattern — a name/size/index key
      // collides after remove-then-re-add and silently drops an upload.
      const key = `photo-${crypto.randomUUID()}`;
      next.set(key, file);
      thumbs.push({ key, previewUrl: URL.createObjectURL(file) });
    }
    setFiles(next);
    setPhotos(thumbs);
  };

  const removePhoto = (key: string) => {
    const next = new Map(files);
    next.delete(key);
    setFiles(next);
    setPhotos((cur) => {
      const gone = cur.find((p) => p.key === key);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return cur.filter((p) => p.key !== key);
    });
  };

  const continueToCustomer = () => {
    if (!cart.bouquetNotes.trim() && !cart.bouquetName.trim()) {
      setError(t("orderCreate.describeBouquetFirst"));
      return;
    }
    setError(null);
    setStep("customer");
  };

  // The order, once created, is LATCHED — a retry after a failed photo
  // upload must resume the uploads, never create a second order (a second
  // tap used to mint a duplicate order, deposit row and courier job).
  // State, not a ref: the Save button relabels to "Retry photo upload".
  const [createdOrder, setCreatedOrder] = useState<CustomerOrder | null>(null);

  const submit = async () => {
    if (!customerName.trim()) {
      setError(t("orderCreate.customerNameRequired"));
      return;
    }
    // OD-104 (owner, 2026-09-27): a phone on every kiosk order — the server
    // refuses one without it too; this says so before the round trip.
    if (!customerPhone.trim()) {
      setError(t("orderCreate.customerPhoneRequired"));
      return;
    }
    if (fulfillment === "COURIER") {
      if (!recipientName.trim() || !deliveryAddress.trim()) {
        setError(t("orderCreate.deliveryNeedsRecipient"));
        return;
      }
      if (!recipientPhone.trim()) {
        setError(t("schedule.addRecipientPhone"));
        return;
      }
      if (deliveryPriceEur.trim() === "" || !Number.isFinite(Number(deliveryPriceEur))) {
        setError(t("orderCreate.setDeliveryPrice"));
        return;
      }
    }
    // Quantities must be real before anything is stored: a blanked qty box
    // used to be counted as €0 on screen but submitted as quantity 1.
    // Prices may stay empty on an order line — "quote it later" is legal.
    const ticketProblem = ticketLinesProblem(ticket.lines, {
      requirePrices: false,
    });
    if (ticketProblem) {
      setError(ticketProblem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let order = createdOrder;
      if (!order) {
        const bouquetPrice =
          cart.bouquetPriceEur.trim() !== "" ? Number(cart.bouquetPriceEur) : undefined;
        const goodsTotal =
          bouquetPrice != null || ticket.lines.length > 0
            ? (bouquetPrice ?? 0) + itemsTotal
            : undefined;
        order = await shopModeApi.floorCreateOrder({
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          customerNotes: customerNote.trim() || undefined,
          bouquetName: cart.bouquetName.trim() || undefined,
          bouquetNotes: cart.bouquetNotes.trim() || undefined,
          fulfillment,
          ...orderWhenPayload(when),
          totalEur: goodsTotal,
          bouquetPriceEur: bouquetPrice,
          lines: ticket.lines.map((l) => ({
            description: l.item.name,
            itemId: l.item.id,
            quantity: Number(l.qtyStr),
            unitPriceEur:
              l.priceStr.trim() !== "" ? Number(l.priceStr) : undefined,
            role: "RETAIL_ITEM",
          })),
          prepaidAmountEur:
            depositEur.trim() !== "" ? Number(depositEur) : undefined,
          prepaidMethod: depositEur.trim() !== "" ? depositMethod : undefined,
          delivery:
            fulfillment === "COURIER"
              ? {
                  recipientName: recipientName.trim(),
                  recipientPhone: recipientPhone.trim(),
                  deliveryAddress: deliveryAddress.trim(),
                  priceEur: Number(deliveryPriceEur),
                }
              : undefined,
        });
        setCreatedOrder(order);
      }
      // Photos ride after creation (the G-15 pattern) — a failed upload must
      // not lose the order, so failures surface as a note, not a rollback.
      // Successful uploads leave the queue, so a retry sends ONLY what is
      // still missing (never a duplicate photo, never a duplicate order).
      let photoFailures = 0;
      const uploadedKeys: string[] = [];
      for (const [key, file] of Array.from(files.entries())) {
        try {
          await shopModeApi.floorUploadOrderReferencePhoto(order.id, file);
          uploadedKeys.push(key);
        } catch {
          photoFailures += 1;
        }
      }
      if (uploadedKeys.length > 0) {
        setFiles((cur) => {
          const next = new Map(cur);
          uploadedKeys.forEach((key) => next.delete(key));
          return next;
        });
      }
      if (photoFailures > 0) {
        setError(
          translate("orderCreate.savedPhotosFailed", {
            code: order.code,
            count: photoFailures,
          }),
        );
        setBusy(false);
        return;
      }
      photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      await onCreated(order);
    } catch (err: unknown) {
      setError(errorText(err, "orderCreate.couldNotSave"));
      setBusy(false);
    }
  };

  return (
    <div className="sf-order-create">
      <div className="day-shifts-row-head">
        <h3 style={{ margin: 0 }}>
          {step === "bouquet"
            ? t("orderCreate.stepBouquet")
            : t("orderCreate.stepCustomer")}
        </h3>
        <button type="button" className="button plain" onClick={onClose} disabled={busy}>
          {t("common.close")}
        </button>
      </div>

      {step === "bouquet" ? (
        <DescribeBouquet
          cart={cart}
          onCartChange={setCart}
          customerNote={customerNote}
          onCustomerNoteChange={setCustomerNote}
          photos={photos}
          photoCap={PHOTO_CAP}
          onAddFiles={addFiles}
          onRemovePhoto={removePhoto}
          error={error}
          onContinue={continueToCustomer}
        />
      ) : (
        <div className="sf-order-create-form">
          {error ? <div className="alert error">{error}</div> : null}

          <div className="field">
            <label htmlFor="sf-oc-name">{t("orderCreate.customerName")}</label>
            <input
              id="sf-oc-name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="field">
            <label htmlFor="sf-oc-phone">{t("orderCreate.phoneRequired")}</label>
            <input
              id="sf-oc-phone"
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="field">
            <label>{t("orderCreate.fulfillment")}</label>
            <div className="button-row">
              {(["PICKUP", "COURIER"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={fulfillment === f ? "button" : "button tinted"}
                  onClick={() => setFulfillment(f)}
                  disabled={busy}
                >
                  {f === "PICKUP" ? t("orderCreate.storePickup") : t("orderCreate.delivery")}
                </button>
              ))}
            </div>
          </div>

          {fulfillment === "COURIER" ? (
            <>
              <div className="field">
                <label htmlFor="sf-oc-recipient">{t("orderCreate.recipient")}</label>
                <input
                  id="sf-oc-recipient"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="field">
                <label htmlFor="sf-oc-recipient-phone">{t("courier.recipientPhone")}</label>
                <input
                  id="sf-oc-recipient-phone"
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="field">
                <label htmlFor="sf-oc-address">{t("orderCreate.deliveryAddress")}</label>
                <input
                  id="sf-oc-address"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="field">
                <label htmlFor="sf-oc-delprice">{t("orderCreate.deliveryPrice")}</label>
                <input
                  id="sf-oc-delprice"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={deliveryPriceEur}
                  onChange={(e) => setDeliveryPriceEur(e.target.value)}
                  disabled={busy}
                />
              </div>
            </>
          ) : null}

          <OrderWhenFields
            idPrefix="sf-oc"
            value={when}
            onChange={setWhen}
            disabled={busy}
          />

          {/* Breaks out of the form's 560px input width: a product grid in a
              560px column is one cramped column of cards (owner 2026-08-09). */}
          <div className="field sf-order-create-items">
            <label>{t("orderCreate.itemsFromShop")}</label>
            <TicketLines
              lines={ticket.lines}
              onLinesChange={ticket.setLines}
              disabled={busy}
              footer={
                <small className="hint">
                  {t("orderCreate.itemsTotal", { amount: eur(itemsTotal) })}
                  {cart.bouquetPriceEur.trim() !== ""
                    ? ` · ${t("orderCreate.orderTotal", {
                        amount: eur(itemsTotal + (Number(cart.bouquetPriceEur) || 0)),
                      })}`
                    : ""}
                </small>
              }
            />
            <div className="button-row">
              <button
                type="button"
                className="button tinted"
                onClick={() => setFinderOpen((cur) => !cur)}
                disabled={busy}
              >
                {finderOpen ? t("orderCreate.hideItems") : t("orderCreate.addItem")}
              </button>
            </div>
            {finderOpen ? (
              <>
                {ticket.stockError ? (
                  <div className="alert error">{ticket.stockError}</div>
                ) : null}
                <StockItemFinder
                  items={ticket.stock}
                  loading={ticket.stockLoading}
                  qtyInTicket={ticket.qtyInTicket}
                  onPick={ticket.pickItem}
                />
              </>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="sf-oc-deposit">{t("orderCreate.depositTaken")}</label>
            <div className="button-row" style={{ alignItems: "center" }}>
              <input
                id="sf-oc-deposit"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                placeholder={t("orderCreate.none")}
                value={depositEur}
                onChange={(e) => setDepositEur(e.target.value)}
                disabled={busy}
                style={{ maxWidth: 140 }}
              />
              {depositEur.trim() !== "" ? (
                <>
                  {(["CASH", "CARD"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={depositMethod === m ? "button" : "button tinted"}
                      onClick={() => setDepositMethod(m)}
                      disabled={busy}
                    >
                      {m === "CASH" ? t("sale.cash") : t("sale.card")}
                    </button>
                  ))}
                </>
              ) : null}
            </div>
            <small className="hint">{t("orderCreate.recordedAsPayment")}</small>
          </div>

          <div className="button-row">
            <button
              type="button"
              className="button plain"
              onClick={() => setStep("bouquet")}
              disabled={busy}
            >
              {t("orderCreate.backToBouquet")}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => void submit()}
              disabled={busy}
            >
              {busy
                ? t("tasks.saving")
                : createdOrder
                  ? t("orderCreate.retryPhotoUpload")
                  : t("orderCreate.saveOrder")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
